import { useLocale } from '../lang/LocalizationProvider';
import styles from './SocialButton.module.scss';

import { env } from '@/utils/env';
import { saveOAuthParams } from '@/utils/carryThroughTracking';
import { appIdentity as appIdentityConsts } from '@/utils/consts/appIdentity';
import analyticsEventHandler from '@/services/analytics/AnalyticsEventHandler';
import { clearApiClient, appIdentityHeaderKey } from '@/utils/api/clearApiClient';

interface SocialButtonProps {
  type: 'google' | 'apple' | 'setapp';
  selectedPlanSku: string;
}

/**
 * https://www.figma.com/file/cKU9Z0zAvOHkVJNCoWlSCs/%F0%9F%96%A5-Clear-DS-(Web-Kit)?node-id=7%3A3007
 */
const SocialButton = ({ type, selectedPlanSku }: SocialButtonProps) => {
  const { selectedLocalization } = useLocale();

  const onClick = () => {
    saveOAuthParams(selectedPlanSku);
    analyticsEventHandler.logOAuthClick(type === 'apple' ? 'apple-id' : type);

    const redirectURl = new URL(`${env.apiURL}/web/v1/account/oauth/${type}/login`);

    redirectURl.searchParams.append('locale', selectedLocalization.toLocaleLowerCase());

    const appIdentity = clearApiClient.defaults.headers[appIdentityHeaderKey];

    redirectURl.searchParams.append(
      'app-identity',
      appIdentity && typeof appIdentity !== 'object' ? appIdentity.toString() : appIdentityConsts.orderAffiliate,
    );

    window.location.href = redirectURl.toString();
  };

  switch (type) {
    case 'google':
    case 'apple':
      return (
        <button
          title={`Sign up with ${type === 'google' ? 'Google' : 'Apple'}`}
          className={`${styles.socialButton} social-button-${type}`}
          type="button"
          onClick={onClick}
        >
          <img src={`/images/social-login/${type}-icon.svg`} width="40" height="40" alt={`${type} icon`} />
        </button>
      );

    case 'setapp':
      /* using cabinet app-identity here, thus the backend will redirect the user to the user profile after auth;
       we don't need no Setapp users on the purchase flow */
      const setappOauthHref = `${env.apiURL}/web/v1/account/oauth/${type}/login?app-identity=${
        appIdentityConsts.cabinet
      }&locale=${selectedLocalization.toLocaleLowerCase()}`;

      return (
        <a
          href={setappOauthHref}
          title="Sign up with Setapp"
          className={`${styles.socialButton} social-button-${type}`}
        >
          <img src={`/images/social-login/${type}-icon.svg`} width="40" height="40" alt={`${type} icon`} />
        </a>
      );

    default:
      return null;
  }
};

export default SocialButton;
